import { useState } from 'react'
import type { MediaAssetSummary } from '@cms/contracts'
import { useCan } from '../auth/session.js'
import { mediaDisplayName, type MediaKind } from '../domain/media.js'
import { useI18n } from '../i18n/context.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { Dialog } from '../ui/overlays.js'
import { Button } from '../ui/primitives.js'
import { MediaBrowser, type MediaBrowserFilters } from './MediaBrowser.js'

/**
 * "Scegli dalla libreria": the library in a dialog. A click selects, a double
 * click (or Enter on the selected tile) uses it; a file uploaded here is
 * selected as soon as it arrives, so "upload and use" is two gestures.
 */
export function MediaPickerDialog({ title, initialKind = 'image', onPick, onClose }: {
  title?: string
  initialKind?: MediaKind | ''
  onPick: (asset: MediaAssetSummary) => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const { siteId } = useSiteWorkspace()
  const canUpload = useCan('cms:media:write')
  const [filters, setFilters] = useState<MediaBrowserFilters>({ q: '', kind: initialKind, status: 'active', page: 1 })
  const [selected, setSelected] = useState<MediaAssetSummary | null>(null)

  const use = (asset: MediaAssetSummary | null) => {
    if (!asset) return
    onPick(asset)
    onClose()
  }

  return <Dialog
    title={title ?? t('media.picker.title')}
    icon="photo_library"
    size="lg"
    onClose={onClose}
    className="media-picker"
    actions={<>
      <span className="media-picker-selection muted">{selected ? t('media.picker.selected', { name: mediaDisplayName(selected) }) : t('media.picker.hint')}</span>
      <span className="spacer"/>
      <Button variant="text" onClick={onClose}>{t('common.cancel')}</Button>
      <Button variant="filled" icon="check" disabled={!selected} onClick={() => use(selected)}>{t('media.picker.use')}</Button>
    </>}
  >
    <MediaBrowser
      siteId={siteId}
      filters={filters}
      onFiltersChange={setFilters}
      onSelect={setSelected}
      onActivate={use}
      selectedId={selected?.id ?? null}
      canUpload={canUpload}
      onUploaded={(result) => setSelected({ ...result.asset, updatedAt: Date.now() })}
    />
  </Dialog>
}
